import type { RoundInfo, TimeoutEvent } from '../types'

const REASON_LABEL: Record<string, string> = {
  t_killed: 'T eliminated',
  ct_killed: 'CT eliminated',
  bomb_exploded: 'Bomb detonated',
  bomb_defused: 'Bomb defused',
  time_ran_out: 'Time expired',
}

export function RoundTimeline({
  rounds,
  ourTeam,
  timeouts = [],
}: {
  rounds: RoundInfo[]
  ourTeam: string
  timeouts?: TimeoutEvent[]
}) {
  // The side switch, not the midpoint of rounds played. Math.ceil(len/2) put the line before
  // round 9 of a 15-round game whose teams swapped after 12 — misplaced on 3 of 5 real matches,
  // and correct on the other two only because they happened to go the full 24.
  // Each row records who won and from which side, so the first round where a given team's side
  // differs from its side in round 1 IS the switch.
  const half = (() => {
    const first = rounds[0]
    if (!first) return 0
    for (let i = 1; i < rounds.length; i++) {
      const r = rounds[i]
      // Same team, other side => the teams have swapped by here.
      if (r.winner_team === first.winner_team && r.winner_side !== first.winner_side) return i
      // Other team, same side => likewise.
      if (r.winner_team !== first.winner_team && r.winner_side === first.winner_side) return i
    }
    return 0 // never switched (a 13-0 half) — no divider rather than a guessed one
  })()
  const toBefore = new Map(timeouts.map((t) => [t.beforeRound, t]))
  return (
    <section
      className="border border-border bg-surface p-5"
      style={{ borderRadius: 'var(--radius)' }}
    >
      <SectionLabel n="02" label="Round timeline" />
      <div className="mt-4 flex gap-1 overflow-x-auto pb-1">
        {rounds.map((r, i) => {
          const weWon = r.winner_team === ourTeam
          const to = toBefore.get(r.round)
          return (
            <div key={r.round} className="flex items-center">
              {i === half && <div className="mx-1 h-9 w-px bg-border-bright" title="Halftime" />}
              {to && <TimeoutMark to={to} ourTeam={ourTeam} />}
              <div
                title={`Round ${r.round} · ${r.winner_team ?? r.winner_side} · ${REASON_LABEL[r.reason] ?? r.reason}`}
                className="flex h-9 w-7 shrink-0 flex-col items-center justify-center text-[10px] font-medium"
                style={{
                  borderRadius: 'var(--radius)',
                  color: weWon ? 'var(--accent-ink)' : '#fff',
                  background: weWon ? 'var(--accent)' : 'var(--loss)',
                }}
              >
                <span className="font-display leading-none">{r.round}</span>
                {r.bomb_planted && <span className="mt-0.5 leading-none opacity-80">◆</span>}
              </div>
            </div>
          )
        })}
      </div>
      <div className="mt-3 flex items-center gap-4 text-[11px] text-text-dim">
        <Legend swatch="var(--accent)" label={`${ourTeam} won`} />
        <Legend swatch="var(--loss)" label="Round lost" />
        <span className="text-text-faint">◆ bomb planted</span>
        {timeouts.length > 0 && <span className="text-text-faint">‖ timeout (hover for who called it)</span>}
      </div>
    </section>
  )
}

/** The break sits between two rounds, so the mark does too — a coach reads the run it interrupted. */
function TimeoutMark({ to, ourTeam }: { to: TimeoutEvent; ourTeam: string }) {
  const ours = to.calledBy === ourTeam
  const streak = to.streakTeam ? `${to.streakTeam} on ${to.streakLen} straight` : 'no run going'
  const by = to.calledBy
    ? `called by ${to.calledBy}${to.callSource === 'chat' ? '' : ' (inferred)'}`
    : 'caller unknown'
  return (
    <div
      title={`Timeout before R${to.beforeRound} — ${by} · ${streak} · ${to.brokeStreak ? 'broke the run' : 'run continued'}`}
      className="mx-0.5 flex h-9 w-3 shrink-0 cursor-default items-center justify-center text-[11px] font-bold leading-none"
      style={{ color: to.calledBy ? (ours ? 'var(--accent)' : 'var(--loss)') : 'var(--text-faint)' }}
    >
      ‖
    </div>
  )
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5" style={{ background: swatch, borderRadius: '2px' }} />
      {label}
    </span>
  )
}

export function SectionLabel({ n, label }: { n: string; label: string }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="font-display text-accent">{n}</span>
      <span className="font-display text-sm uppercase tracking-[0.18em] text-text-dim">{label}</span>
    </div>
  )
}
